import { BUCKET_COLORS } from "./CityBubbleChart";
import { chartDomain, chartScales, toPoints, placeLabels } from "./cityChartGeometry";

export const VIDEO_W = 1920, VIDEO_H = 1080;
const PAD = 60;
const BOX = { l: 120, t: 222, iw: VIDEO_W - 120 - 70, ih: 683 };
const INK = "#111111", MUTED = "#6B6F68", GRID = "#E6E6E2", BG = "#FFFFFF", TEAL = "#006874", RED = "#C8333F";
const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const clamp01 = (v) => Math.max(0, Math.min(1, v));
const smooth = (t, a, b) => { const u = clamp01((t - a) / (b - a)); return u * u * (3 - 2 * u); };
const hex2rgba = (hex, a) => `rgba(${parseInt(hex.slice(1, 3), 16)},${parseInt(hex.slice(3, 5), 16)},${parseInt(hex.slice(5, 7), 16)},${a})`;

/** Pre-compute per-frame points + labels once (same geometry as the SVG chart, 1920×1080 box). */
export function prepareTimeline(frames, { sansFamily, measureCtx }) {
  const dom = chartDomain(frames.flatMap((f) => f.rows));
  const scales = chartScales(BOX, dom, 17);
  measureCtx.font = `700 19px ${sansFamily}`;
  const measure = (name) => measureCtx.measureText(name).width;
  const prepared = frames.map((f) => {
    const pts = toPoints(f.rows, BOX, scales);
    return { year: f.year, pts, byCity: Object.fromEntries(pts.map((p) => [p.city, p])), labels: placeLabels(pts, measure, 20) };
  });
  return { dom, scales, prepared };
}

const drawText = (ctx, text, x, y, { font, color, align = "left", baseline = "alphabetic", stroke }) => {
  ctx.font = font; ctx.fillStyle = color; ctx.textAlign = align; ctx.textBaseline = baseline;
  if (stroke) { ctx.lineJoin = "round"; ctx.lineWidth = 5; ctx.strokeStyle = stroke; ctx.strokeText(text, x, y); }
  ctx.fillText(text, x, y);
};

/**
 * Draw one video frame. `i` = index of the frame being left, `t` ∈ [0,1] = progress of the transition to frame
 * i+1 (t = 0 → hold on frame i). Bubbles present in both years glide (eased), entering ones grow in, leaving ones
 * shrink out; labels of the target year fade in during the second half of the move.
 */
export function drawTimelineFrame(ctx, tl, i, t, { strings, highlightIt, marked, serifFamily, sansFamily }) {
  const { scales, prepared } = tl;
  const A = prepared[i], B = prepared[Math.min(i + 1, prepared.length - 1)];
  const e = ease(t);
  const yearShown = t < 0.5 ? A.year : B.year;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.fillStyle = BG; ctx.fillRect(0, 0, VIDEO_W, VIDEO_H);
  ctx.fillStyle = TEAL; ctx.fillRect(0, 0, 10, VIDEO_H);

  // header
  drawText(ctx, strings.title(yearShown), PAD, 92, { font: `700 50px ${serifFamily}`, color: INK });
  drawText(ctx, strings.subtitle(yearShown), PAD, 134, { font: `400 21px ${sansFamily}`, color: MUTED });
  drawText(ctx, yearShown, VIDEO_W - 70, 96, { font: `700 72px ${serifFamily}`, color: TEAL, align: "right" });
  // timeline progress
  const n = prepared.length, tx0 = PAD, tx1 = VIDEO_W - 70, ty = 164;
  ctx.strokeStyle = GRID; ctx.lineWidth = 3; ctx.beginPath(); ctx.moveTo(tx0, ty); ctx.lineTo(tx1, ty); ctx.stroke();
  prepared.forEach((f, k) => {
    const x = tx0 + ((tx1 - tx0) * k) / (n - 1);
    ctx.fillStyle = k <= i ? TEAL : GRID; ctx.beginPath(); ctx.arc(x, ty, 5, 0, Math.PI * 2); ctx.fill();
    drawText(ctx, f.year, x, ty + 24, { font: `${k === i || (t > 0.5 && k === i + 1) ? 700 : 400} 14px ${sansFamily}`, color: MUTED, align: k === 0 ? "left" : k === n - 1 ? "right" : "center" });
  });
  const px = tx0 + ((tx1 - tx0) * (i + (i < n - 1 ? e : 0))) / (n - 1);
  ctx.fillStyle = TEAL; ctx.beginPath(); ctx.arc(px, ty, 9, 0, Math.PI * 2); ctx.fill();

  // grid + axes
  ctx.lineWidth = 1.5;
  scales.xt.forEach((v) => {
    const x = scales.x(v);
    ctx.strokeStyle = GRID; ctx.beginPath(); ctx.moveTo(x, BOX.t); ctx.lineTo(x, BOX.t + BOX.ih); ctx.stroke();
    drawText(ctx, String(v), x, BOX.t + BOX.ih + 28, { font: `400 17px ${sansFamily}`, color: MUTED, align: "center" });
  });
  scales.yt.forEach((v) => {
    const y = scales.y(v);
    ctx.strokeStyle = GRID; ctx.beginPath(); ctx.moveTo(BOX.l, y); ctx.lineTo(BOX.l + BOX.iw, y); ctx.stroke();
    drawText(ctx, String(v), BOX.l - 14, y + 6, { font: `400 17px ${sansFamily}`, color: MUTED, align: "right" });
  });
  drawText(ctx, strings.x(yearShown), BOX.l + BOX.iw - 8, BOX.t + BOX.ih - 14, { font: `700 18px ${sansFamily}`, color: INK, align: "right", stroke: BG });
  ctx.save(); ctx.translate(34, BOX.t + BOX.ih / 2); ctx.rotate(-Math.PI / 2);
  drawText(ctx, strings.y, 0, 0, { font: `700 18px ${sansFamily}`, color: INK, align: "center" });
  ctx.restore();

  // bubbles: union of both frames, interpolated
  const cities = new Set([...Object.keys(A.byCity), ...Object.keys(B.byCity)]);
  const drawn = [];
  cities.forEach((c) => {
    const a = A.byCity[c], b = B.byCity[c];
    let cx, cy, r, alpha = 1, row;
    if (a && b) { cx = a.cx + (b.cx - a.cx) * e; cy = a.cy + (b.cy - a.cy) * e; r = a.r + (b.r - a.r) * e; row = t < 0.5 ? a : b; }
    else if (a) { cx = a.cx; cy = a.cy; r = a.r * (1 - e); alpha = 1 - e; row = a; }
    else { cx = b.cx; cy = b.cy; r = b.r * e; alpha = e; row = b; }
    if (r < 0.5) return;
    drawn.push({ cx, cy, r, alpha, row });
  });
  drawn.sort((p, q) => q.r - p.r).forEach(({ cx, cy, r, alpha, row }) => {
    const it = row.country_iso2 === "IT", faded = highlightIt && !it;
    ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = hex2rgba(BUCKET_COLORS[row.bucket] || BUCKET_COLORS.under_2, (faded ? 0.18 : 0.82) * alpha);
    ctx.fill();
    ctx.lineWidth = highlightIt && it ? 3.5 : 1.8;
    ctx.strokeStyle = highlightIt && it ? hex2rgba(TEAL, alpha) : hex2rgba("#FFFFFF", alpha);
    ctx.stroke();
    const slot = marked?.a === row.city ? "a" : marked?.b === row.city ? "b" : null;
    if (slot) {
      ctx.setLineDash([7, 5]); ctx.lineWidth = 3; ctx.strokeStyle = hex2rgba(slot === "a" ? TEAL : RED, alpha);
      ctx.beginPath(); ctx.arc(cx, cy, r + 8, 0, Math.PI * 2); ctx.stroke(); ctx.setLineDash([]);
    }
  });

  // labels: A fades out in the first half, B fades in during the second half
  const drawLabels = (F, alpha) => {
    if (alpha <= 0.01) return;
    F.pts.forEach((p) => {
      const L = F.labels[p.city];
      if (!L) return;
      const faded = highlightIt && p.country_iso2 !== "IT";
      drawText(ctx, p.name, p.cx + L.dx, p.cy + L.dy, {
        font: `${p.country_iso2 === "IT" && highlightIt ? 800 : 700} 19px ${sansFamily}`,
        color: hex2rgba(faded ? "#969692" : INK, alpha), align: L.anchor === "middle" ? "center" : L.anchor === "end" ? "right" : "left",
        stroke: hex2rgba(BG, alpha),
      });
    });
  };
  if (t === 0 || i === prepared.length - 1) drawLabels(A, 1);
  else { drawLabels(A, 1 - smooth(t, 0, 0.45)); drawLabels(B, smooth(t, 0.55, 1)); }

  // legend + footer
  let x = PAD;
  drawText(ctx, strings.legend, x, VIDEO_H - 86, { font: `700 17px ${sansFamily}`, color: INK });
  x += ctx.measureText(strings.legend).width + 18;
  Object.entries(strings.buckets).forEach(([k, label]) => {
    ctx.fillStyle = BUCKET_COLORS[k]; ctx.beginPath(); ctx.arc(x + 8, VIDEO_H - 92, 8, 0, Math.PI * 2); ctx.fill();
    drawText(ctx, label, x + 22, VIDEO_H - 86, { font: `400 17px ${sansFamily}`, color: MUTED });
    x += ctx.measureText(label).width + 48;
  });
  ctx.strokeStyle = GRID; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.moveTo(PAD, VIDEO_H - 60); ctx.lineTo(VIDEO_W - 70, VIDEO_H - 60); ctx.stroke();
  drawText(ctx, strings.footer, PAD, VIDEO_H - 30, { font: `400 16px ${sansFamily}`, color: MUTED });
}
